import { t } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import { Box, Divider, Stack, Typography } from "@mui/material";
import type { PriceCheckResponse, PriceComponent } from "@/types/api";
import { formatDateRange } from "@/utils/booking";
import { getExtraPeriodLabel, isNoPriceBillingType } from "@/utils/extras";
import {
  formatCurrency,
  formatPriceComponentType,
  getAltPrice,
  groupComponentsByObjectId,
} from "@/utils/pricing";

interface PriceBreakdownProps {
  price: PriceCheckResponse;
}

export default function PriceBreakdown({ price }: PriceBreakdownProps) {
  const { i18n } = useLingui();
  const formatPrice = (value: number) => formatCurrency(value, price.currency, i18n.locale);
  const formatPriceAlt = (value: number) =>
    price.currency_alt ? formatCurrency(value, price.currency_alt.currency, i18n.locale) : null;

  const allComponents = price.components || [];
  const allAltComponents = price.currency_alt?.components || [];
  const componentGroups = groupComponentsByObjectId(allComponents);

  const renderComponent = (component: PriceComponent) => {
    const componentIndex = allComponents.indexOf(component);
    const altPrice = getAltPrice(component.object_id, componentIndex, allAltComponents);
    const hasAltCurrency = altPrice > 0 && price.currency_alt;

    let priceLabel = "";
    if (
      component.object_type === "extra" &&
      isNoPriceBillingType(component.extra_fields?.billing_type ?? "")
    ) {
      priceLabel = getExtraPeriodLabel(
        component.extra_fields?.billing_type ?? "",
        i18n,
        component.amount,
      );
    } else if (component.included) {
      priceLabel = t`Included`;
    }

    return (
      <Box
        key={`${component.code}-${component.date_from}-${component.date_until}`}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          justifyContent: "space-between",
        }}
      >
        <Typography variant="body2">
          {component.amount > 1 ? `${component.amount} x` : ""} {component.name}
        </Typography>
        <Stack sx={{ alignItems: "end" }}>
          <Typography variant="body2" fontWeight={600}>
            {priceLabel ? priceLabel : formatPrice(component.price)}
          </Typography>
          {hasAltCurrency && (
            <Typography variant="body2" color="text.secondary">
              ({formatPriceAlt(altPrice)})
            </Typography>
          )}
        </Stack>
      </Box>
    );
  };

  const renderGroupedComponent = (components: PriceComponent[]) => {
    if (components.length === 0) return null;

    const firstComponent = components[0]!;

    const totalPrice = components.reduce((sum, c) => sum + c.price, 0);
    const totalPriceAlt = components.reduce((sum, c) => {
      const componentIndex = allComponents.indexOf(c);
      return (
        sum + (componentIndex >= 0 ? getAltPrice(c.object_id, componentIndex, allAltComponents) : 0)
      );
    }, 0);
    const hasAltCurrency = totalPriceAlt > 0 && price.currency_alt;

    return (
      <Box key={`group-${firstComponent.object_id}`}>
        <Box
          sx={{
            display: "flex",
            alignItems: "top",
            gap: 1,
            justifyContent: "space-between",
          }}
        >
          <Typography variant="body2">
            {firstComponent.amount > 1 ? `${firstComponent.amount} x` : ""} {firstComponent.name}
          </Typography>
          <Stack sx={{ alignItems: "end" }}>
            <Typography variant="body2" fontWeight={600}>
              {formatPrice(totalPrice)}
            </Typography>
            {hasAltCurrency && (
              <Typography variant="body2" color="text.secondary">
                ({formatPriceAlt(totalPriceAlt)})
              </Typography>
            )}
          </Stack>
        </Box>
        <Box sx={{ mt: 0.5 }}>
          <Stack spacing={0.5}>
            {components.map((component) => {
              if (!component.date_from || !component.date_until) return null;
              return (
                <Typography
                  key={`${component.code}-${component.date_from}-${component.date_until}`}
                  variant="body2"
                >
                  {formatDateRange(
                    [new Date(component.date_from), new Date(component.date_until)],
                    i18n.locale,
                  )}
                  : {component.days} nights x {formatPrice(component.single_price)}
                </Typography>
              );
            })}
          </Stack>
        </Box>
      </Box>
    );
  };

  return (
    <Box>
      <Stack spacing={2}>
        {componentGroups.map((group, index) => {
          const type = group[0]!.object_type;
          const prevGroup = index > 0 ? componentGroups[index - 1] : undefined;
          const prevType = prevGroup?.[0]?.object_type;
          const nextGroup =
            index < componentGroups.length - 1 ? componentGroups[index + 1] : undefined;
          const nextType = nextGroup?.[0]?.object_type;
          const showHeader = type && type !== prevType;
          const showDivider = type && type === nextType;
          const title = formatPriceComponentType(type, i18n);

          return (
            <Box key={`group-${group[0]?.object_id}`}>
              {showHeader && (
                <>
                  <Typography variant="body1" sx={{ textTransform: "capitalize" }}>
                    {title}
                  </Typography>
                  <Divider sx={{ mt: 1, mb: 1.5, borderColor: "black" }} />
                </>
              )}
              {group.length === 1
                ? renderComponent(group[0] as PriceComponent)
                : renderGroupedComponent(group)}
              {showDivider && <Divider sx={{ mt: 1.5 }} />}
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
}
